import React, { useState } from "react";
import { Tabs, Tab, Box, Typography } from "@mui/material";
import KpiTable from "./KpiTable";
import { useSalesContext } from "../../SalesContext";

interface TabData {
  green: string;
  yellow: string;
  red: string;
}

interface DataItem {
  managerName: string;
  totalCount: number;
  inGreen: number;
  inYellow: number;
  inGreenPercent: string;
  inYellowPercent: string;
  inRedPercent: string;
  inRed: string;
}

interface TableData {
  tabTitle: string;
  data: DataItem[];
}

const tabBackgroundColors: Record<string, string> = {
  MR: "#E8EEF7",
  FLM: "#E6F7FF",
  SLM: "#F3F7E8",
};

const TabComponent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("MR");
  const { userRole, kpiData } = useSalesContext();

  console.log("userRole:", userRole);

  const defaultTabData: TabData = { green: "0%", yellow: "0%", red: "0%" };

  const normalizeCardData = (cardData: any): TabData => {
    if (!cardData) return defaultTabData;
    return {
      green: cardData.Green || cardData.green || "0%",
      yellow: cardData.Yellow || cardData.yellow || "0%",
      red: cardData.Red || cardData.red || "0%",
    };
  };

  const normalizeTableData = (tableData: any[], tab: string): TableData => {
    if (!tableData || !Array.isArray(tableData))
      return { tabTitle: tab, data: [] };
    return {
      tabTitle: tab,
      data: tableData.map((item) => ({
        managerId: item.ManagerId,
        managerName: item.ManagerName || "Unknown Manager",
        totalCount: item.TotalCount || 0,
        inGreen: item.InGreen || 0,
        inYellow: item.InYellow || 0,
        inGreenPercent: item.InGreenPercent || "0%",
        inYellowPercent: item.InYellowPercent || "0%",
        inRed: item.InRed || 0,
        inRedPercent: item.InRedPercent || "0%",
      })),
    };
  };

  const tabData: Record<string, TabData> = {
    MR: normalizeCardData(kpiData?.CardData?.MR),
    FLM: normalizeCardData(kpiData?.CardData?.FLM),
    SLM: normalizeCardData(kpiData?.CardData?.SLM),
  };

  const tableData: Record<string, TableData> = {
    MR: normalizeTableData(kpiData?.TableData?.MR, "MR"),
    FLM: normalizeTableData(kpiData?.TableData?.FLM, "FLM"),
    SLM: normalizeTableData(kpiData?.TableData?.SLM, "SLM"),
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const isTabDisabled = (tab: string): boolean => {
    if (userRole === "MR") {
      return true;
    }
    if (userRole === "FLM") {
      return tab === "FLM" || tab === "SLM";
    }
    if (userRole === "SLM") {
      return tab === "SLM";
    }
    return false;
  };

  return (
    <Box sx={{ margin: "0 auto", padding: 2 }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          marginBottom: 2,
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        {Object.keys(tabData).map((tab) => (
          <Tab
            key={tab}
            label={
              <Box
                sx={{
                  textAlign: "center",
                  width: "100%",
                  "&:focus": { outline: "none" },
                  "&:active": { outline: "none" },
                  "&.Mui-selected": { outline: "none" },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#3B459E",
                    fontSize: "14px",
                    fontWeight: "bold",
                    textAlign: "left",
                    paddingLeft: "20px",
                  }}
                >
                  {tab}
                </Typography>
                <Box
                  sx={{
                    backgroundColor:
                      activeTab === tab ? "#19198C" : tabBackgroundColors[tab],
                    color: activeTab === tab ? "#FFFFFF !important" : "#000",
                    borderRadius: "20px",
                    width: "100%",
                    padding: 2,
                    display: "flex",
                    justifyContent: "space-around",
                    mt: 1,
                    height: "134px",
                    textAlign: "center",
                    opacity: isTabDisabled(tab) ? 0.5 : 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: activeTab === tab ? "#FFFFFF" : "textSecondary",
                      }}
                    >
                      Green
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ color: activeTab === tab ? "#FFFFFF" : "inherit" }}
                    >
                      {tabData[tab].green || "0%"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: activeTab === tab ? "#FFFFFF" : "textSecondary",
                      }}
                    >
                      Yellow
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ color: activeTab === tab ? "#FFFFFF" : "inherit" }}
                    >
                      {tabData[tab].yellow || "0%"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: activeTab === tab ? "#FFFFFF" : "textSecondary",
                      }}
                    >
                      Red
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ color: activeTab === tab ? "#FFFFFF" : "inherit" }}
                    >
                      {tabData[tab].red || "0%"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            }
            value={tab}
            disabled={isTabDisabled(tab)}
            sx={{
              padding: "12px 6px",
            }}
          />
        ))}
      </Tabs>

      {/* Table Section */}
      <Box sx={{ border: "1px solid #ddd", borderRadius: 1, padding: 2 }}>
        {tableData[activeTab].data.length > 0 ? (
          <KpiTable tableData={tableData[activeTab]} />
        ) : (
          <Typography variant="body1" sx={{ textAlign: "center", padding: 2 }}>
            No data available for {activeTab}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TabComponent;
